import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../Styles/SurveyFeedbackForm.css";
import { surveysAPI, fileAPI } from "../../api/index"; // adjust path to match your project

// UI shows Satisfied/Neutral/Dissatisfied; backend wants an int 1-5.
const RATING_TO_SCORE = { Satisfied: 5, Neutral: 3, Dissatisfied: 1 };

const CATEGORIES = [
  "Road Development",
  "Drinking Water",
  "Electricity",
  "Health Camp",
  "Education",
  "Sanitation",
  "General",
];
const FEEDBACK_TYPES = ["Complaint", "Suggestion", "Appreciation"];
const RATINGS = ["Satisfied", "Neutral", "Dissatisfied"];
const MANDALS = ["Kandukur", "Gudluru", "Ulavapadu"];
const STATUSES = ["Reviewed", "Pending"];

const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const INITIAL_FORM = {
  name: "",
  mobile: "",
  voterId: "",
  category: "",
  type: "",
  rating: "",
  village: "",
  mandal: "",
  date: "",
  status: "",
  details: "",
};

// Order used to focus the first invalid field on submit (same as the form layout)
const FIELD_ORDER = [
  "name",
  "mobile",
  "voterId",
  "category",
  "type",
  "rating",
  "village",
  "mandal",
  "date",
  "status",
  "details",
];

// These fields show a format error immediately while typing
const LIVE_VALIDATE_FIELDS = ["voterId", "village"];

const NAME_REGEX = /^[A-Za-z\s.']+$/;
const VILLAGE_REGEX = /^[A-Za-z\s]+$/;
const VOTER_ID_REGEX = /^[A-Z]{3}[0-9]{7}$/;
const MOBILE_REGEX = /^[6-9][0-9]{9}$/;

// ---------- Field validation rules ----------
const validateField = (name, value) => {
  const v = typeof value === "string" ? value.trim() : value;

  switch (name) {
    case "name":
      if (!v) return "Respondent name is required";
      if (v.length < 3) return "Name must be at least 3 characters";
      if (v.length > 100) return "Name must not exceed 100 characters";
      if (!NAME_REGEX.test(v)) return "Name can contain only alphabets, spaces, '.' and '''";
      return "";

    case "mobile":
      if (!v) return "Mobile number is required";
      if (!/^\d+$/.test(v)) return "Mobile number must contain digits only";
      if (v.length !== 10) return "Mobile number must be exactly 10 digits";
      if (!MOBILE_REGEX.test(v)) return "Mobile number must start with 6, 7, 8 or 9";
      return "";

    case "voterId":
      if (!v) return ""; // optional
      if (!VOTER_ID_REGEX.test(v))
        return "Enter a valid Voter ID (3 letters + 7 digits, e.g. ABC1234567)";
      return "";

    case "category":
      if (!v) return "Please select a Category";
      if (!CATEGORIES.includes(v)) return "Please select a valid Category";
      return "";

    case "type":
      if (!v) return "Please select a Feedback Type";
      if (!FEEDBACK_TYPES.includes(v)) return "Please select a valid Feedback Type";
      return "";

    case "rating":
      if (!v) return "Please select a Satisfaction Rating";
      if (!RATINGS.includes(v)) return "Please select a valid Rating";
      return "";

    case "village":
      if (!v) return "Village is required";
      if (!VILLAGE_REGEX.test(v)) return "Village name can contain only alphabets (A-Z)";
      if (v.length < 2) return "Village name must be at least 2 characters";
      if (v.length > 100) return "Village name must not exceed 100 characters";
      return "";

    case "mandal":
      if (!v) return "Please select a Mandal";
      if (!MANDALS.includes(v)) return "Please select a valid Mandal";
      return "";

    case "date": {
      if (!v) return "Date is required";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "Enter a valid date";
      if (d.getFullYear() < 1990) return "Date cannot be before 1990";
      if (d.getFullYear() > 2100) return "Date cannot be after 2100";
      return "";
    }

    case "status":
      if (!v) return "Please select a Status";
      if (!STATUSES.includes(v)) return "Please select a valid Status";
      return "";

    case "details":
      if (!v) return "Feedback details are required";
      if (v.length < 10) return "Feedback details must be at least 10 characters";
      if (v.length > 1000) return "Feedback details must not exceed 1000 characters";
      return "";

    default:
      return "";
  }
};

const validateAll = (data) => {
  const errors = {};
  FIELD_ORDER.forEach((name) => {
    const msg = validateField(name, data[name]);
    if (msg) errors[name] = msg;
  });
  return errors;
};

const SurveyFeedbackForm = () => {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoError, setPhotoError] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(""); // server / API errors only

  const showError = (name) => touched[name] && errors[name];
  const inputClass = (name) => (showError(name) ? "input-error" : "");

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Auto-format specific fields
    if (name === "voterId") value = value.toUpperCase().replace(/\s/g, "");
    if (name === "mobile") value = value.replace(/\D/g, "");

    setFormData((prev) => ({ ...prev, [name]: value }));

    const isLive = LIVE_VALIDATE_FIELDS.includes(name);
    if (touched[name] || isLive) {
      if (isLive && !touched[name]) {
        setTouched((prev) => ({ ...prev, [name]: true }));
      }
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  /* ---------- Photo (optional) ---------- */
  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview("");
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      clearPhoto();
      setPhotoError("");
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      clearPhoto();
      setPhotoError("Only JPG, PNG or WEBP images are allowed");
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      clearPhoto();
      setPhotoError("Photo must not exceed 5MB");
      return;
    }

    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoError("");
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setTouched({});
    clearPhoto();
    setPhotoError("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate every field and mark all as touched so every error is shown
    const newErrors = validateAll(formData);
    setErrors(newErrors);
    setTouched(FIELD_ORDER.reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    const firstInvalid = FIELD_ORDER.find((f) => newErrors[f]);
    if (firstInvalid || photoError) {
      toast.error("Please fix the highlighted fields");
      const target = firstInvalid ? firstInvalid : "photo";
      const el = document.querySelector(`[name="${target}"]`);
      if (el) el.focus();
      return;
    }

    setSubmitting(true);
    try {
      let photoUrl = null;
      if (photo) {
        const uploadRes = await fileAPI.upload(photo, "surveys");
        photoUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url;
      }

      await surveysAPI.create({
        respondent_name: formData.name.trim(),
        mobile_number: formData.mobile.trim(),
        epic_no: formData.voterId.trim() || null,
        category: formData.category,
        feedback_type: formData.type,
        satisfaction_rating: RATING_TO_SCORE[formData.rating] ?? null,
        village_name: formData.village.trim(),
        mandal_name: formData.mandal,
        survey_date: formData.date, // <input type="date"> already gives YYYY-MM-DD
        status: formData.status.toLowerCase(),
        feedback_details: formData.details.trim(),
        photo_url: photoUrl,
      });

      // Success toast — stays visible after navigating to the table page
      toast.success("Form submitted successfully", { toastId: "survey-form-success" });

      handleReset();
      navigate("/dashboard/SurveyFeedbackTable");
    } catch (err) {
      console.error("Survey save failed:", err);
      setError(
        err.response?.data?.detail || err.message || "Something went wrong while saving the feedback."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="survey-form-container">
      <div className="survey-form-card">
        <div className="survey-form-header">
          <h2>Add New Survey / Feedback</h2>
          <p>Record citizen feedback, suggestions, or complaints</p>
          <button
            type="button"
            className="survey-back-btn"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>

        {error && <div className="survey-form-error">{error}</div>}

        {/* noValidate -> disables browser default popups so our custom messages show */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="survey-form-grid">
            <div className="survey-form-group">
              <label>Respondent Name *</label>
              <input
                type="text"
                name="name"
                placeholder="Enter Name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("name")}
                maxLength={100}
              />
              {showError("name") && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="survey-form-group">
              <label>Mobile Number *</label>
              <input
                type="tel"
                inputMode="numeric"
                name="mobile"
                placeholder="Enter 10-digit Mobile Number"
                maxLength="10"
                value={formData.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mobile")}
              />
              {showError("mobile") && <span className="error-text">{errors.mobile}</span>}
            </div>

            <div className="survey-form-group">
              <label>Voter ID *</label>
              <input
                type="text"
                name="voterId"
                placeholder="Enter voter ID (e.g. ABC1234567)"
                maxLength="10"
                value={formData.voterId}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("voterId")}
              />
              {showError("voterId") && <span className="error-text">{errors.voterId}</span>}
            </div>

            <div className="survey-form-group">
              <label>Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("category")}
              >
                <option value="">Select Category</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {showError("category") && <span className="error-text">{errors.category}</span>}
            </div>

            <div className="survey-form-group">
              <label>Feedback Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("type")}
              >
                <option value="">Select Type</option>
                {FEEDBACK_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              {showError("type") && <span className="error-text">{errors.type}</span>}
            </div>

            <div className="survey-form-group">
              <label>Satisfaction Rating *</label>
              <select
                name="rating"
                value={formData.rating}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("rating")}
              >
                <option value="">Select Rating</option>
                {RATINGS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              {showError("rating") && <span className="error-text">{errors.rating}</span>}
            </div>

            

            <div className="survey-form-group">
              <label>Mandal *</label>
              <select
                name="mandal"
                value={formData.mandal}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mandal")}
              >
                <option value="">Select Mandal</option>
                {MANDALS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {showError("mandal") && <span className="error-text">{errors.mandal}</span>}
            </div>
            <div className="survey-form-group">
              <label>Village *</label>
              <input
                type="text"
                name="village"
                placeholder="Enter Village (alphabets only)"
                value={formData.village}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("village")}
                maxLength={100}
              />
              {showError("village") && <span className="error-text">{errors.village}</span>}
            </div>

            <div className="survey-form-group">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("date")}
                min="1990-01-01"
                max="2100-12-31"
              />
              {showError("date") && <span className="error-text">{errors.date}</span>}
            </div>

            <div className="survey-form-group">
              <label>Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("status")}
              >
                <option value="">Select Status</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {showError("status") && <span className="error-text">{errors.status}</span>}
            </div>

            <div className="survey-form-group">
              <label>Upload Photo (Optional)</label>
              <input
                ref={photoInputRef}
                type="file"
                name="photo"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoChange}
                className={photoError ? "input-error" : ""}
              />
              {photoError && <span className="error-text">{photoError}</span>}
              <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                JPG, PNG, WEBP (Max 5MB)
              </small>

              {photoPreview && (
                <div className="photo-preview-wrapper">
                  <img src={photoPreview} alt="Selected" className="photo-preview-img" />
                  <button
                    type="button"
                    className="photo-remove-btn"
                    onClick={clearPhoto}
                    aria-label="Remove photo"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="survey-form-group survey-full-width">
            <label>Feedback Details *</label>
            <textarea
              rows="4"
              name="details"
              placeholder="Describe the feedback, complaint, or suggestion"
              value={formData.details}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("details")}
              maxLength={1000}
            ></textarea>
            {showError("details") && <span className="error-text">{errors.details}</span>}
            <small className="char-count">{formData.details.length}/1000</small>
          </div>

          <div className="survey-btn-group">
            <button type="submit" className="survey-submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
            <button type="button" className="survey-reset-btn" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SurveyFeedbackForm;